import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  Stack,
  Tabs,
  Tab,
  Typography,
  Alert,
  Divider,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTitle } from "../../hooks/useTitle";
import ProjectDetailsPanel from "./ProjectDetailsPanel";
import ProjectMessagesPanel from "./ProjectMessagesPanel";
import { Project, ProjectStatus } from "../../types/project";
import { useGetProjectByIdQuery } from "../../services/project.service";
import ProjectTalentsPanel from "./ProjectTalentsPanel";
import qs from "qs";
import { formatWithTimezone } from "../../utils/dateHelper";
import ChipProjectStatus from "../../components/ChipProjectStatus";
import { CardBase, OrangeLinearProgress, Button } from "../../components/global";
import ModalSearchCandidateByAI from "./ModalSearchCandidateByAI";

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectMeta = ({
  icon,
  value,
  isMobile,
}: {
  icon: React.ReactNode;
  value: string;
  isMobile?: boolean;
}) => (
  <Chip
    icon={icon as React.ReactElement}
    label={value}
    size="small"
    variant="outlined"
    sx={{
      mr: { xs: 0.5, sm: 1 },
      mb: { xs: 0.5, sm: 1 },
      color: "text.secondary",
      borderColor: "divider",
      fontSize: { xs: "0.7rem", sm: "0.75rem" },
      height: { xs: 24, sm: 32 },
      "& .MuiChip-icon": {
        color: "text.secondary",
        fontSize: { xs: "0.875rem", sm: "1rem" },
      },
      "& .MuiChip-label": {
        padding: { xs: "0 6px", sm: "0 8px" },
      },
    }}
  />
);

// const StatusChip = ({ status }: { status?: string }) => {
//   status = status?.toLowerCase();
//   const style = { fontWeight: 500 };

//   if (status == ProjectStatus.DRAFT)
//     return <Chip label={"DRAFT"} sx={style} color="default" />;
//   if (status == ProjectStatus.PUBLISHED)
//     return <Chip label={"PUBLISHED"} sx={style} color="secondary" />;
//   if (status == ProjectStatus.IN_PROGRESS)
//     return <Chip label={"IN PROGRESS"} sx={style} color="primary" />;
//   if (status == ProjectStatus.COMPLETED)
//     return <Chip label={"COMPLETED"} sx={style} color="info" />;
//   if (status == ProjectStatus.CANCELED)
//     return <Chip label={"CANCELED"} sx={style} color="error" />;
//   else return <Chip label={status} sx={style} color="default" />;
// };

const ProjectHeader = ({
  data,
  loading,
  onOpenAiSearch,
}: {
  data?: Project;
  loading: Boolean;
  onOpenAiSearch?: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { city, state, zip } = React.useMemo(() => {
    if (!!data?.customer) {
      return {
        city: data.customer.city || "?",
        state: data.customer.state || "?",
        zip: data.customer.zip || "?",
      };
    } else if (!!data?.location) {
      return {
        city: data.location.city || "?",
        state: data.location.state || "?",
        zip: data.location.zip || "?",
      };
    } else {
      return {
        city: "?",
        state: "?",
        zip: "?",
      };
    }
  }, [data]);

  if (loading)
    return (
      <CardBase useGlassCard={false} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Stack direction="column" spacing={{ xs: 1.5, sm: 2 }}>
          <Skeleton variant="text" sx={{ width: { xs: "60%", sm: "30%" }, height: { xs: 32, sm: 40 } }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Box sx={{ width: { xs: 80, sm: 100 }, height: { xs: 24, sm: 32 } }}>
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: { xs: 100, sm: 120 }, height: { xs: 24, sm: 32 } }}>
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: { xs: 120, sm: 150 }, height: { xs: 24, sm: 32 } }}>
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
            </Box>
          </Stack>
        </Stack>
      </CardBase>
    );

  return (
    <CardBase
      useGlassCard={false}
      sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 } }}
    >
      <Stack direction="column" spacing={{ xs: 1.5, sm: 2 }}>
        {isMobile ? (
          <>
            <Box>
              <ChipProjectStatus status={data?.status as ProjectStatus} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "#F4A259",
                fontWeight: 600,
                fontSize: "1.1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {`#${data?.id} ${data?.title}`}
            </Typography>
          </>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <ChipProjectStatus status={data?.status as ProjectStatus} />
            <Typography
              variant="h5"
              sx={{
                color: "#F4A259",
                fontWeight: 600,
                flex: 1,
                fontSize: "1.5rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {`#${data?.id} ${data?.title}`}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={{ xs: 0.5, sm: 1 }}>
          <ProjectMeta
            icon={<BusinessCenterIcon />}
            value={!!data?.is_private ? "Private Job" : "Public Job"}
            isMobile={isMobile}
          />
          <ProjectMeta
            icon={<BusinessCenterIcon />}
            value={data?.type == "fixed" ? "Fixed Price" : "Hourly Rate"}
            isMobile={isMobile}
          />
          <ProjectMeta
            icon={<CalendarMonthIcon />}
            value={formatWithTimezone(data?.start_date, data?.timezone) || "N/A"}
            isMobile={isMobile}
          />
          <ProjectMeta
            icon={<LocationCityIcon />}
            value={`${city}, ${state} ${zip}`}
            isMobile={isMobile}
          />
        </Stack>
        {data?.status == ProjectStatus.CANCELED && (
          <Alert severity="error" sx={{ mt: { xs: 0.5, sm: 1 }, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
            Cancel Reason: {data?.canceled_reason || "n/a"}
          </Alert>
        )}
        {data?.status == ProjectStatus.PUBLISHED && (
          <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={onOpenAiSearch}
              sx={{
                backgroundColor: "#F4A259",
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                padding: { xs: "6px 12px", sm: "8px 16px" },
                "&:hover": {
                  backgroundColor: "#E8944A",
                },
              }}
            >
              Search Candidate by AI
            </Button>
          </Box>
        )}
      </Stack>
    </CardBase>
  );
};

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const ProjectDetailPage = () => {
  const { projectId } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [value, setValue] = React.useState(0);
  const [aiSearchModalOpen, setAiSearchModalOpen] = React.useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { data, isLoading, isFetching } = useGetProjectByIdQuery(
    parseInt(projectId!),
    {
      skip: !projectId,
    }
  );

  useTitle(`Project #${data?.id}`, `${data?.title} | Project`);

  React.useEffect(() => {
    const params = qs.parse(window.location.href.split("?")[1]);
    console.log(params);
    if (params.t) {
      setValue(Number(params.t));
    }
  }, []);

  return (
    <Box>
      <ProjectHeader
        data={data}
        loading={isLoading || isFetching}
        onOpenAiSearch={() => setAiSearchModalOpen(true)}
      />
      {(isLoading || isFetching) && <OrangeLinearProgress sx={{ mb: 2 }} />}
      <CardBase useGlassCard={false} sx={{ p: 0 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="inherit"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 500,
              color: "text.secondary",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              minHeight: { xs: 40, sm: 48 },
              padding: { xs: "8px 12px", sm: "12px 16px" },
              "&.Mui-selected": {
                color: "#F4A259 !important",
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#F4A259",
            },
            "& .MuiTabs-scrollButtons": {
              color: "#F4A259",
            },
          }}
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Talents" {...a11yProps(1)} />
          <Tab label="Messages" {...a11yProps(2)} />
          {/* <Tab label="Log" {...a11yProps(3)} /> */}
        </Tabs>
        <TabPanel value={value} index={0} dir={theme.direction}>
          <ProjectDetailsPanel data={data} />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          {!!data && <ProjectTalentsPanel project={data} />}
        </TabPanel>
        <TabPanel value={value} index={2} dir={theme.direction}>
          {!!data && <ProjectMessagesPanel project={data} />}
        </TabPanel>
        {/* <TabPanel value={value} index={3} dir={theme.direction}>
          <ProjectHistoriesPanel />
        </TabPanel> */}
      </CardBase>
      {data && (
        <ModalSearchCandidateByAI
          open={aiSearchModalOpen}
          onClose={() => setAiSearchModalOpen(false)}
          project={data}
        />
      )}
    </Box>
  );
};
export default ProjectDetailPage;
